import { ReactNode, useEffect, useState } from "react";
import useLocalStorage from "../../hooks/useLocalStorage";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { DarkModeContext } from "../../contexts/DarkModeContext";

/**
 * Perform initialization of things related to Themes and dark mode. Wrap children in a context provider that allows
 * them to consume and update the settings related to dark mode
 */
export const DarkModeEnabledProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [darkModeEnabledPreference, setDarkModeEnabledPreference] =
    useLocalStorage<boolean | null>("darkModeEnabledPreference", null);

  const [darkModeEnabled, setDarkModeEnabled] = useState<boolean>(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  useEffect(() => {
    const r: HTMLElement | null = document.querySelector(":root");
    r?.style.setProperty(
      "--primary-color",
      process.env.REACT_APP_WHITELABEL_COLOR ?? "#F20505",
    );
    if (document.title === "AI Chat") {
      document.title =
        (process.env.REACT_APP_WHITELABEL_NAME ?? "Expedient") + " Chat";
    }
  }, []);

  const darkTheme = createTheme({
    palette: {
      mode: darkModeEnabledPreference ?? darkModeEnabled ? "dark" : "light",
      primary: {
        main: process.env.REACT_APP_WHITELABEL_COLOR ?? "#F20505",
      },
      secondary: {
        main: "#E0C2FF",
        light: "#F5EBFF",
        contrastText: "#47008F",
      },
    },
  });

  const toggleDarkModeEnabled = () => {
    setDarkModeEnabledPreference((prev) => !(prev ?? darkModeEnabled));
  };
  return (
    <ThemeProvider theme={darkTheme}>
      <DarkModeContext.Provider
        value={{
          darkModeEnabled: darkModeEnabledPreference ?? darkModeEnabled,
          toggleDarkModeEnabled: toggleDarkModeEnabled,
        }}
      >
        {children}
      </DarkModeContext.Provider>
    </ThemeProvider>
  );
};
